import { useEffect, useState } from "react";
import api from "../../../lib/api";
import { Calendar, Clock, CheckCircle2, XCircle, Trash2, Mail, Phone, MessageSquare } from "lucide-react";

const STATUSES = [
  { id: "all", label: "Toutes" },
  { id: "pending", label: "En attente" },
  { id: "confirmed", label: "Confirmées" },
  { id: "rejected", label: "Refusées" },
];

export default function BookingsManager() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [open, setOpen] = useState(null);
  const [rejectNote, setRejectNote] = useState("");

  const load = () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/bookings${q}`).then(({ data }) => setItems(data));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const updateStatus = async (id, status, admin_note = "") => {
    await api.put(`/bookings/${id}/status`, { status, admin_note });
    setOpen(null);
    setRejectNote("");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette réservation ?")) return;
    await api.delete(`/bookings/${id}`);
    setOpen(null);
    load();
  };

  const badge = (s) => {
    const map = {
      pending: { label: "En attente", cls: "bg-[#C87A5E]/15 text-[#C87A5E]" },
      confirmed: { label: "Confirmée", cls: "bg-[#8A9A86]/15 text-[#8A9A86]" },
      rejected: { label: "Refusée", cls: "bg-red-100 text-red-700" },
    };
    const cfg = map[s] || { label: s, cls: "bg-gray-100" };
    return <span className={`text-xs px-2 py-0.5 rounded-full ${cfg.cls}`}>{cfg.label}</span>;
  };

  return (
    <div data-testid="bookings-manager">
      <div className="mb-8">
        <h1 className="font-serif text-4xl">Réservations</h1>
        <p className="text-[#4A3F35] mt-2">Demandes de séances envoyées via le widget de réservation.</p>
      </div>

      <div className="bg-[#FDFBF7] rounded-2xl p-2 mb-6 flex flex-wrap gap-1 border border-[#2D2A26]/10 w-fit">
        {STATUSES.map((s) => (
          <button key={s.id} onClick={() => setFilter(s.id)} className={`px-4 py-2 rounded-xl text-sm ${filter === s.id ? "bg-[#2D2A26] text-white" : "hover:bg-[#F6F3EC]"}`} data-testid={`bk-filter-${s.id}`}>
            {s.label}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="bg-[#FDFBF7] rounded-2xl border border-[#2D2A26]/10 p-12 text-center text-[#4A3F35]/60">
          <Calendar size={36} className="mx-auto mb-3 text-[#4A3F35]/30" />
          Aucune réservation {filter !== "all" ? `(${STATUSES.find(s => s.id === filter)?.label.toLowerCase()})` : ""}.
        </div>
      ) : (
        <div className="bg-[#FDFBF7] rounded-2xl border border-[#2D2A26]/10 overflow-hidden divide-y divide-[#2D2A26]/10">
          {items.map((b) => (
            <button key={b.id} onClick={() => setOpen(b)} className="w-full text-left p-5 hover:bg-[#F6F3EC] transition flex gap-4 items-start" data-testid={`booking-${b.id}`}>
              <div className="w-12 h-12 rounded-full bg-[#C87A5E]/10 text-[#C87A5E] flex items-center justify-center shrink-0">
                <Calendar size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-medium">{b.name}</span>
                  {badge(b.status)}
                  {b.service && <span className="text-xs text-[#C87A5E] uppercase tracking-wider">{b.service}</span>}
                </div>
                <div className="text-sm text-[#4A3F35] mt-1 capitalize">
                  {new Date(b.date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} · {b.time}
                </div>
                {b.message && <div className="text-sm text-[#4A3F35]/70 mt-1 line-clamp-1">{b.message}</div>}
              </div>
              <div className="text-xs text-[#4A3F35]/50 shrink-0">{new Date(b.created_at).toLocaleDateString("fr-FR")}</div>
            </button>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => setOpen(null)}>
          <div className="bg-[#FDFBF7] rounded-3xl p-8 w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="booking-detail">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h3 className="font-serif text-2xl flex items-center gap-3">{open.name} {badge(open.status)}</h3>
                <a href={`mailto:${open.email}`} className="text-[#C87A5E] link-underline text-sm inline-flex items-center gap-1.5 mt-1"><Mail size={12} /> {open.email}</a>
                {open.phone && <div className="text-sm text-[#4A3F35]/70 mt-1 inline-flex items-center gap-1.5"><Phone size={12} /> {open.phone}</div>}
              </div>
              <button onClick={() => remove(open.id)} className="text-red-700 text-sm inline-flex items-center gap-1"><Trash2 size={13} /> Supprimer</button>
            </div>

            <div className="bg-[#F6F3EC] rounded-xl p-4 mb-5">
              <div className="overline mb-2">Créneau demandé</div>
              <div className="flex items-center gap-4 font-medium capitalize">
                <span className="inline-flex items-center gap-2"><Calendar size={14} className="text-[#C87A5E]" /> {new Date(open.date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
                <span className="inline-flex items-center gap-2"><Clock size={14} className="text-[#C87A5E]" /> {open.time}</span>
              </div>
              {open.service && <div className="mt-2 text-sm">Type : <strong>{open.service}</strong></div>}
            </div>

            {open.message && (
              <>
                <div className="overline mb-2 flex items-center gap-1.5"><MessageSquare size={12} /> Message</div>
                <p className="bg-[#FDFBF7] border border-[#2D2A26]/10 rounded-xl p-4 leading-relaxed whitespace-pre-line text-sm">{open.message}</p>
              </>
            )}

            {open.admin_note && (
              <div className="mt-4 text-sm text-[#4A3F35]/70 italic">Note admin : {open.admin_note}</div>
            )}

            {open.status === "pending" && (
              <div className="mt-6 pt-6 border-t border-[#2D2A26]/10 space-y-3">
                <button onClick={() => updateStatus(open.id, "confirmed")} className="btn-primary w-full justify-center !bg-[#8A9A86] !border-[#8A9A86]" data-testid="confirm-booking">
                  <CheckCircle2 size={16} /> Confirmer la réservation (envoie un email)
                </button>
                <div>
                  <input
                    placeholder="Raison du refus (optionnel, sera envoyé au client)"
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.target.value)}
                    className="w-full border border-[#2D2A26]/15 rounded-xl px-3 py-2 text-sm mb-2"
                  />
                  <button onClick={() => updateStatus(open.id, "rejected", rejectNote)} className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-red-300 text-red-700 hover:bg-red-50 transition" data-testid="reject-booking">
                    <XCircle size={16} /> Refuser
                  </button>
                </div>
              </div>
            )}

            {open.status !== "pending" && (
              <button onClick={() => updateStatus(open.id, "pending")} className="btn-ghost mt-6 w-full justify-center text-sm">
                Repasser en "En attente"
              </button>
            )}

            <div className="text-xs text-[#4A3F35]/50 mt-6">Reçue le {new Date(open.created_at).toLocaleString("fr-FR")}</div>
          </div>
        </div>
      )}
    </div>
  );
}
